import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, HandHeart, ShieldCheck, ArrowLeft } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { createDonationCheckout, getDonationPackages } from "@/lib/api";

const PRESETS = [
  { id: "seed", amount: 50, label: "Seed", desc: "One meal for a grieving family." },
  { id: "shield", amount: 100, label: "Shield", desc: "Care package + family follow-up." },
  { id: "guardian", amount: 250, label: "Guardian", desc: "Full week of family meal support." },
  { id: "blue_partner", amount: 500, label: "Blue Partner", desc: "Rapid crisis response for a department." },
];

export default function Donate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("shield");
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [recurring, setRecurring] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const preAmount = params.get("amount");
    if (preAmount) {
      const found = PRESETS.find((p) => String(p.amount) === preAmount);
      if (found) setSelected(found.id);
      else {
        setSelected("custom");
        setCustom(preAmount);
      }
    }
    // Fetch packages just to verify backend
    getDonationPackages().catch(() => {});
  }, [params]);

  const isCustom = selected === "custom";
  const effectiveAmount = isCustom
    ? parseFloat(custom || "0")
    : PRESETS.find((p) => p.id === selected)?.amount || 0;

  const submit = async () => {
    if (isCustom && (!effectiveAmount || effectiveAmount < 5)) {
      toast.error("Please enter an amount of at least $5.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        package_id: isCustom ? null : selected,
        custom_amount: isCustom ? effectiveAmount : null,
        recurring,
        origin_url: window.location.origin,
        donor_name: donor.name || null,
        donor_email: donor.email || null,
      };
      const res = await createDonationCheckout(payload);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Payment session failed to start.";
      toast.error(typeof msg === "string" ? msg : "Payment failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <main className="pt-24 md:pt-32 pb-24">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            data-testid="donate-back"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-black transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="eyebrow text-blue-700 mb-4">Give to the Mission</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                Feed a family. <br />
                <span className="text-blue-700">Restore a soul.</span>
              </h1>
              <p className="mt-6 text-slate-600 leading-relaxed text-lg">
                Your support makes our work possible. Every dollar helps provide meals, groceries,
                family check-ins, and crisis response for officers and their families.
              </p>

              <div className="mt-10 space-y-4">
                {[
                  { icon: HandHeart, t: "100% mission-directed", b: "Every gift fuels meals, care packages, and rapid response." },
                  { icon: ShieldCheck, t: "Secure checkout", b: "Payments processed by Stripe. Your card details never touch our servers." },
                ].map((x) => (
                  <div key={x.t} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-black text-white shrink-0">
                      <x.icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-display font-bold text-slate-900">{x.t}</div>
                      <p className="text-sm text-slate-600">{x.b}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="border border-slate-200 p-8 md:p-10">
                <div className="eyebrow text-slate-500 mb-4">Choose an amount</div>

                <div className="grid grid-cols-2 gap-4">
                  {PRESETS.map((p) => {
                    const active = selected === p.id;
                    return (
                      <button
                        key={p.id}
                        data-testid={`preset-${p.id}`}
                        onClick={() => setSelected(p.id)}
                        className={`text-left p-6 border-2 transition-all ${
                          active
                            ? "border-black bg-black text-white"
                            : "border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className="font-display text-3xl font-black">${p.amount}</span>
                          <span className={`eyebrow ${active ? "text-blue-400" : "text-blue-700"}`}>{p.label}</span>
                        </div>
                        <p className={`mt-2 text-xs leading-relaxed ${active ? "text-slate-300" : "text-slate-600"}`}>
                          {p.desc}
                        </p>
                      </button>
                    );
                  })}

                  <button
                    data-testid="preset-custom"
                    onClick={() => setSelected("custom")}
                    className={`text-left p-6 border-2 transition-all col-span-2 ${
                      isCustom ? "border-black bg-black text-white" : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-2xl font-black">Custom Amount</span>
                      <span className={`eyebrow ${isCustom ? "text-blue-400" : "text-blue-700"}`}>You Decide</span>
                    </div>
                    {isCustom && (
                      <div className="mt-4 flex items-center gap-2">
                        <span className="text-2xl font-display font-black">$</span>
                        <input
                          data-testid="custom-amount-input"
                          type="number"
                          min={5}
                          max={25000}
                          step="1"
                          value={custom}
                          onChange={(e) => setCustom(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Enter amount"
                          className="bg-transparent border-b-2 border-white/50 focus:border-blue-400 outline-none text-2xl font-display font-black text-white placeholder-white/40 flex-1 py-1"
                        />
                      </div>
                    )}
                  </button>
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="eyebrow text-slate-500">Your Name (optional)</label>
                    <input
                      data-testid="donor-name"
                      value={donor.name}
                      onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-base font-medium"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-slate-500">Email (for receipt)</label>
                    <input
                      data-testid="donor-email"
                      type="email"
                      value={donor.email}
                      onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-base font-medium"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <label className="mt-6 flex items-center gap-3 cursor-pointer select-none" data-testid="recurring-toggle-wrap">
                  <input
                    data-testid="recurring-toggle"
                    type="checkbox"
                    checked={recurring}
                    onChange={(e) => setRecurring(e.target.checked)}
                    className="w-4 h-4 accent-blue-700"
                  />
                  <span className="text-sm text-slate-700">
                    I'd like to make this a recurring monthly gift <span className="text-slate-400">(we'll follow up to set it up)</span>
                  </span>
                </label>

                <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="eyebrow text-slate-500">Total</div>
                    <div className="font-display text-4xl font-black text-slate-900">
                      ${effectiveAmount ? effectiveAmount.toLocaleString(undefined, { minimumFractionDigits: 0 }) : "0"}
                    </div>
                  </div>
                  <button
                    data-testid="donate-checkout-btn"
                    disabled={loading || !effectiveAmount}
                    onClick={submit}
                    className="inline-flex items-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {loading ? "Redirecting…" : "Donate Securely"}
                  </button>
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  You'll be redirected to Stripe's secure checkout. Currency: USD.
                </p>
              </div>

              <Link
                to="/"
                data-testid="donate-return-link"
                className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-black link-brut"
              >
                Return to home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
